/**
 * Email Event Form Component
 *
 * Form for creating and editing email event configurations.
 * Includes validation, template preview, and variable helpers.
 */

import {
  Stack,
  Select,
  Textarea,
  TextInput,
  Switch,
  Button,
  Group,
  Tabs,
  Text,
  Alert,
  Paper,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  rem
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDeviceFloppy, IconEye, IconInfoCircle, IconCopy, IconCheck, IconWand } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import type { EmailEventType } from "../../../../../../../types/email-event.d";
import {
  EMAIL_EVENT_METADATA,
  getEventDisplayName,
  getEventsByCategory,
  CATEGORY_NAMES
} from "../../email-event-metadata";
import type { EmailEventFormData, EmailEventWithId } from "../../schema/email-event.types";
import { EmailAccountSelector } from "../email-account-selector";
import { TemplatePreview } from "../template-preview";

/**
 * Props for EmailEventForm
 */
export interface EmailEventFormProperties {
  /** Licenciada ID for fetching email accounts */
  licenciadaId: string;
  /** Initial values for editing (undefined for create) */
  initialValues?: EmailEventWithId;
  /** Callback when form is submitted */
  onSubmit: (values: EmailEventFormData) => void | Promise<void>;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Whether the form is in submitting state */
  loading?: boolean;
}

/**
 * EmailEventForm Component
 * @param root0
 * @param root0.licenciadaId
 * @param root0.initialValues
 * @param root0.onSubmit
 * @param root0.onCancel
 * @param root0.loading
 * @example
 * <EmailEventForm
 *   licenciadaId="lic123"
 *   initialValues={existingEvent}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export function EmailEventForm({
  licenciadaId,
  initialValues,
  onSubmit,
  onCancel,
  loading = false
}: EmailEventFormProperties) {
  const isEditing = Boolean(initialValues);
  const [activeTab, setActiveTab] = useState<string | null>("form");

  const form = useForm<EmailEventFormData>({
    initialValues: initialValues
      ? {
          event_type: initialValues.event_type,
          subject_template: initialValues.subject_template,
          body_template: initialValues.body_template,
          email_account_id: initialValues.email_account_id,
          enabled: initialValues.enabled
        }
      : {
          event_type: "" as EmailEventType,
          subject_template: "",
          body_template: "",
          email_account_id: "",
          enabled: true
        },
    validate: {
      event_type: (value) => (value ? null : "Selecione um tipo de evento"),
      subject_template: (value) => {
        if (!value || value.trim().length === 0) {
          return "Assunto é obrigatório";
        }
        if (value.length > 200) {
          return "Assunto muito longo (máximo 200 caracteres)";
        }
        return null;
      },
      body_template: (value) => {
        if (!value || value.trim().length === 0) {
          return "Corpo do email é obrigatório";
        }
        return null;
      },
      email_account_id: (value) => (value ? null : "Selecione uma conta de email")
    }
  });

  const handleSubmit = form.onSubmit(async (values) => {
    await onSubmit(values);
  });

  // Group event types by category for the select
  const eventsByCategory = getEventsByCategory();
  const eventSelectData = Object.entries(eventsByCategory).flatMap(([category, eventTypes]) => [
    { value: `__category_${category}`, label: CATEGORY_NAMES[category] || category, disabled: true },
    ...eventTypes.map((eventType) => ({
      value: eventType,
      label: `  ${getEventDisplayName(eventType)}`
    }))
  ]);

  const selectedEventType = form.values.event_type;
  const metadata = selectedEventType ? EMAIL_EVENT_METADATA[selectedEventType] : null;
  const handleUseSubjectExample = useCallback(() => {
    form.setFieldValue("subject_template", metadata?.subject_example || "");
  }, [form, metadata?.subject_example]);

  const handleUseBodyExample = useCallback(() => {
    form.setFieldValue("body_template", metadata?.body_example || "");
  }, [form, metadata?.body_example]);

  return (
    <form onSubmit={handleSubmit}>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="form" leftSection={<IconDeviceFloppy size={16} />}>
            Configuração
          </Tabs.Tab>
          <Tabs.Tab
            value="preview"
            leftSection={<IconEye size={16} />}
            disabled={!selectedEventType || !form.values.subject_template}
          >
            Preview
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="form" pt="md">
          <Stack gap="md">
            {metadata && (
              <Alert icon={<IconInfoCircle size={16} />} title={metadata.display_name} color="primary" variant="light">
                <Text size="sm">{metadata.description}</Text>
              </Alert>
            )}

            {/* Event Type Select */}
            <Select
              label="Tipo de Evento"
              placeholder="Selecione o tipo de evento"
              data={eventSelectData}
              disabled={isEditing} // Cannot change event type when editing
              required
              {...form.getInputProps("event_type")}
            />

            {/* Subject Template */}
            <TextInput
              label="Assunto do Email"
              placeholder={metadata?.subject_example || "Ex: Você foi mencionado | {{ideia.titulo}}"}
              description="Use {{variavel}} para inserir valores dinâmicos"
              required
              rightSection={
                metadata?.subject_example && (
                  <Tooltip label="Usar exemplo">
                    <ActionIcon variant="subtle" color="gray" onClick={handleUseSubjectExample}>
                      <IconWand size={16} />
                    </ActionIcon>
                  </Tooltip>
                )
              }
              {...form.getInputProps("subject_template")}
            />

            {/* Body Template */}
            <Textarea
              label="Corpo do Email (HTML)"
              placeholder={metadata?.body_example || "<p>Olá {{colaborador.nome}},</p><p>Você foi mencionado...</p>"}
              description="HTML com variáveis {{variavel}}"
              required
              autosize
              minRows={2}
              maxRows={10}
              rightSection={
                metadata?.body_example && (
                  <Tooltip label="Usar exemplo">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={handleUseBodyExample}
                      style={{ alignSelf: "flex-start", marginTop: rem(4) }}
                    >
                      <IconWand size={16} />
                    </ActionIcon>
                  </Tooltip>
                )
              }
              {...form.getInputProps("body_template")}
            />

            {/* Available Variables */}
            {metadata && (
              <Paper withBorder p="md" bg="gray.0">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Variáveis Disponíveis para este Evento
                  </Text>
                  <Stack gap={4}>
                    {metadata.available_variables.map((variable) => (
                      <Group key={variable.name} gap="xs">
                        <Code block style={{ flex: 1 }}>
                          {`{{${variable.name}}}`} - {variable.description}
                        </Code>
                        <CopyButton value={`{{${variable.name}}}`} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Copiado" : "Copiar"} withArrow position="right">
                              <ActionIcon color={copied ? "success" : "gray"} variant="subtle" onClick={copy}>
                                {copied ? (
                                  <IconCheck style={{ width: rem(16) }} />
                                ) : (
                                  <IconCopy style={{ width: rem(16) }} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}

            {/* Email Account Selector */}
            <EmailAccountSelector licenciadaId={licenciadaId} required {...form.getInputProps("email_account_id")} />

            {/* Enabled Switch */}
            <Switch
              label="Evento ativo"
              description="Quando ativo, emails serão enviados automaticamente"
              {...form.getInputProps("enabled", { type: "checkbox" })}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="preview" pt="md">
          {selectedEventType && form.values.subject_template ? (
            <TemplatePreview
              eventType={selectedEventType}
              subjectTemplate={form.values.subject_template}
              bodyTemplate={form.values.body_template}
            />
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              Preencha o tipo de evento e o assunto para ver o preview
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Form Actions */}
      <Group justify="flex-end" mt="xl">
        <Button variant="subtle" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" loading={loading}>
          {isEditing ? "Atualizar Evento" : "Criar Evento"}
        </Button>
      </Group>
    </form>
  );
}

EmailEventForm.displayName = "EmailEventForm";
